import { createSlice } from '@reduxjs/toolkit'
import {
  getVisitsCensus,
  getBetaVisitStatus,
  getFacilities,
  getDischargeCodes,
  getDocuments,
} from './censusThunk'
import { format } from 'date-fns'

const initialState = {
  data: [],
  status: 'idle',
  error: null,
  visitStatuses: {},
  dischargeCodes: [],
  inPatients: [],
  activeDate: format(new Date(), 'yyyy-MM-dd'),
  activeTab: null,
  allFacilitiesList: [],
  facilityObj: [],
  doRefresh: 0,
  patientSearchText: '',
  censusFilters: JSON.parse(localStorage.getItem('censusFilters')) || {},
  sortStatus: { columnName: null, ascending: true },
  providerSortStatus: { columnName: null, ascending: true },
  showFavoritesOnly: false,
}
const censusSlice = createSlice({
  name: 'census',
  initialState,
  reducers: {
    setActiveDate: (state, action) => {
      state.activeDate = action.payload
      localStorage.setItem('currentCensusDate', action.payload)
    },
    setSortStatus: (state, action) => {
      state.sortStatus = action.payload
    },
    setProviderSortStatus: (state, action) => {
      state.providerSortStatus = action.payload
    },

    setActiveFacilityId: (state, action) => {
      if (action.payload) {
        localStorage.setItem('currentCensusFacilityId', action.payload)
      } else {
        localStorage.removeItem('currentCensusFacilityId')
      }
      state.activeTab = action.payload
    },

    setCensusFilters: (state, action) => {
      state.censusFilters = action.payload
      // the saddest requirement of page refresh persistence
      localStorage.setItem('censusFilters', JSON.stringify(action.payload))
    },
    setPatientSearchText: (state, action) => {
      state.patientSearchText = action.payload
    },
    setShowFavoritesOnly: (state, action) => {
      state.showFavoritesOnly = action.payload
    },
    resetCensusStore: (state) => {
      localStorage.removeItem('currentCensusFacilityId')
      localStorage.setItem('currentCensusDate', format(new Date(), 'yyyy-MM-dd'))
      state.activeDate = initialState.activeDate
      state.activeTab = initialState.activeTab
      state.allFacilitiesList = initialState.allFacilitiesList
      state.inPatients = initialState.inPatients
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFacilities.pending, (state, action) => {
        state.isloading = true
      })
      .addCase(getFacilities.fulfilled, (state, action) => {
        state.isloading = false
        state.allFacilitiesList = action.payload?.allFacilitiesList || []
      })
      .addCase(getFacilities.rejected, (state, action) => {
        state.isloading = false
        state.isError = true
        if (action.payload) {
          state.errorMsg = action.payload
        } else {
          state.errorMsg = action.error.message?.toString()
        }
      })
      .addCase(getVisitsCensus.pending, (state, action) => {
        state.isloading = true
      })
      .addCase(getVisitsCensus.fulfilled, (state, action) => {
        state.isloading = false
        if (action.payload && state.activeDate === action.payload.service_date) {
          state.inPatients = action.payload?.inPatients || []
          state.doRefresh = state.doRefresh + 1
          state.docByFacilityList = action.payload?.docByFacilityList
          state.fileDisplayName = action.payload?.fileDisplayName
        }
      })
      .addCase(getVisitsCensus.rejected, (state, action) => {
        state.isloading = false
        state.isError = true
        if (action.payload) {
          // Being that we passed in ValidationErrors to rejectType in `createAsyncThunk`, the payload will be available here.
          state.errorMsg = action.payload
        } else {
          state.errorMsg = action.error.message?.toString()
        }
      })
      .addCase(getBetaVisitStatus.fulfilled, (state, action) => {
        state.visitStatuses = action?.payload?.visitStatuses?.results[0]?.values
      })
      .addCase(getDischargeCodes.pending, (state, action) => {
        state.isloading = true
      })
      .addCase(getDischargeCodes.fulfilled, (state, action) => {
        state.isloading = false
        state.dischargeCodes = action?.payload?.dischargeCodes?.results[0]?.values
      })
      .addCase(getDischargeCodes.rejected, (state, action) => {
        state.isloading = false
        state.isError = true
        if (action.payload) {
          state.errorMsg = action.payload
        } else {
          state.errorMsg = action.error.message?.toString()
        }
      })
      .addCase(getDocuments.fulfilled, (state, action) => {
        state.docByFacilityList = action.payload?.docByFacilityList
        state.fileDisplayName = action.payload?.fileDisplayName
      })
  },
})

export const {
  setActiveFacilityId,
  setActiveDate,
  setPatientSearchText,
  setCensusFilters,
  resetCensusStore,
  setSortStatus,
  setProviderSortStatus,
  setShowFavoritesOnly,
} = censusSlice.actions
export default censusSlice.reducer
